<script>
import { mapActions, mapGetters } from 'vuex'
import Action from '@/components/Action'
import Icon from '@/components/Icon'
import NumberField from '@/components/NumberField'
import ContentLoading from '@/components/ContentLoading'
import ModalProductImage from '@/components/ModalProductImage'
import ModalUpdateCartItemQuantity from '@/components/ModalUpdateCartItemQuantity'
import ModalConfirmation from '@/components/ModalConfirmation'
import logger from '@/logger/index'
import { getUserErrorMessage } from '@/services/userError'

export default {
  name: 'ProductDetail',

  props: { searchNewProduct: Function },

  components: {
    Action,
    Icon,
    NumberField,
    ContentLoading,
    ModalProductImage,
    ModalUpdateCartItemQuantity,
    ModalConfirmation,
  },

  data () {
    return {
      quantity: 1,
      loadingPrice: false,
      loadingProduct: false,
      loadingImage: false,
      productImage: null,
      showImageModal: false,
      showModalQtde: false,
      showModalConfirm: false,
      itens: [],
      updateProduct: null,
    }
  },

  computed: {
    ...mapGetters('products', ['getSelectedProduct', 'getPriceAdd', 'getShowPriceWithAdd']),
    ...mapGetters('orders', ['getSelectedOrder', 'isLoadingOrderCart', 'getOrderCartError']),
    ...mapGetters('userPermissions', ['getEnableParts', 'getFavoriteCompany', 'getBlockSale']),
    ...mapGetters('userTips', ['hasSeenTip']),

    showPriceWithAdd () {
      return this.getShowPriceWithAdd
    },

    computedClasses () {
      return {
        [this.$classes['active']]: this.showPriceWithAdd,
      }
    },

    ipiAliquota () {
      return this.getSelectedProduct.ipi_aliquota
    },

    ipiAmount () {
      // preço unitário base × (IPI% / 100)
      const ipi = (this.priceRule.price || 0) * ((this.getSelectedProduct.ipi_aliquota || 0) / 100)
      return this.formatDecimal(ipi)
    },

    priceWithIpi () {
      // preço base + valor do IPI
      return (this.priceRule.price || 0) + this.ipiAmount
    },

    percentualProductPrice () {
      // Calcula o percentual de preço do produto
      return 1 + (this?.getPriceAdd / 100)
    },

    priceRule () {
      return this.getSelectedProduct?.price.find(price => this.quantity >= price.min_limit && this.quantity <= price.max_limit) || {}
    },

    subtotal () {
      let subTotal = 0
      if (this.showPriceWithAdd) {
        subTotal = ((this.quantity * this.priceRule.price) * this.percentualProductPrice)
      } else {
        subTotal = ((this.quantity * this.priceRule.price))
      }
      return this.formatDecimal(subTotal)
    },

    totalIpi () {
      return this.ipiAmount * this.quantity
    },

    subtotalWithIpi () {
      const subtotalWithIpi = (this.subtotal + this.totalIpi)
      return this.formatDecimal(subtotalWithIpi)
    },

    stockFavoriteCompany () {
      const stock = this.getSelectedProduct?.stock

      if (!Array.isArray(stock)) return null

      return stock.find(s => s?.company_id === this.getFavoriteCompany) || null
    },

    totalStockQuantity () {
      return this.getSelectedProduct?.stock.reduce(
        (total, stock) => total + stock.quantity,
        0
      )
    },

    isMultipleOfFraction () {
      const frac = this.getSelectedProduct?.fraction
      const qty = this.quantity

      if (!frac || frac <= 0 || typeof frac !== 'number') return false
      if (!qty || qty <= 0 || typeof qty !== 'number') return false

      if (qty < frac) return false

      return qty % frac === 0
    },

    hasStockObjects () {
      const stock = this.getSelectedProduct?.stock

      if (stock && typeof stock === 'object' && !Array.isArray(stock)) {
        return typeof stock.message === 'string' && stock.message !== ''
      }

      return false
    },

    isOrderFinalized () {
      return this.getSelectedOrder?.status === 'Finalizado'
    },

    // Motivo pelo qual não dá pra adicionar; null quando o carrinho está utilizável.
    // Critério é "pendente/falhou" e não "já carregou", porque OrdersDetails.vue
    // usa este mesmo componente e nunca despacha getOrderCart.
    cartUnavailableMessage () {
      if (this.getOrderCartError) return 'Não foi possível carregar o carrinho. Use "Tentar novamente" no painel de pedidos.'
      if (this.isLoadingOrderCart) return 'Carregando carrinho…'
      return null
    },

    addProductLabel () {
      return this.isLoadingOrderCart ? 'Carregando carrinho' : 'Adicionar'
    },

    isAddProductLoading () {
      return this.loadingProduct || this.isLoadingOrderCart
    },

    disableBtnAddProduct () {
      // Adicionar com o carrinho em voo cairia no ensureOrderExists e criaria um
      // pedido duplicado em paralelo ao que ainda vem do servidor.
      return this.loadingProduct ||
        this.hasStockObjects ||
        this.isOrderFinalized ||
        !!this.cartUnavailableMessage
    },

    isPriceUnavailable () {
      const price = this.getSelectedProduct?.price
      if (!price || !Array.isArray(price) || price.length === 0) return true

      const hasValidPrice = price.some(rule => rule?.price != null && Number(rule.price) > 0)

      return !hasValidPrice
    },
  },

  mounted () {
    const fraction = this.getSelectedProduct?.fraction
    this.quantity = (fraction && fraction > 0) ? fraction : 0

    // TODO: Chamar um feedback se a fração não for configurada
    if (!fraction || fraction <= 0) {
      this.setFeedback({
        title: 'Erro ao carregar produto',
        message: 'Parece que houve um problema ao carregar o produto. Por favor, tente novamente mais tarde.',
        type: 'error',
      })
    }

    // Exibe dica instrucional sobre acréscimo financeiro (apenas primeira vez)
    this.$nextTick(() => {
      if (this.$refs.priceToggleBtn) {
        const isConfigured = this.getPriceAdd > 0
        const content = isConfigured
          ? 'Clique aqui para alternar entre o preço com e sem acréscimo financeiro. Isso permite visualizar rapidamente o impacto do acréscimo nos valores.'
          : 'Esta funcionalidade permite alternar entre o preço com e sem acréscimo financeiro. Configure o acréscimo nas configurações para habilitar este recurso.'

        this.showTip({
          key: 'priceToggle',
          title: 'Nova Funcionalidade',
          content,
          targetRef: this.$refs.priceToggleBtn.$el || this.$refs.priceToggleBtn,
          position: 'left',
          delay: 700,
          startDate: '13/10/2025',
          validForMonths: 3,
        })
      }
    })
  },

  watch: {
    'getSelectedProduct.image': {
      handler (newImage) {
        if (newImage && this.getSelectedProduct?.id) {
          this.fetchProductImage()
        }
      },
      immediate: true,
    },

    getPriceAdd: {
      handler (newValue) {
        // Se getPriceAdd for false e showPriceWithAdd estiver true, reseta para false
        if (!newValue && this.getShowPriceWithAdd) {
          this.resetShowPriceWithAdd()
        }
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions(['setHeaderBack', 'setFeedback']),
    ...mapActions('products', ['getProduct', 'checkProductPrice', 'getProductImage', 'toggleShowPriceWithAdd', 'resetShowPriceWithAdd']),
    ...mapActions('orders', ['addProduct', 'updateProductQuantity']),
    ...mapActions('userTips', ['showTip']),

    getSimilarProductPrice (similarProduct) {
      if (!similarProduct.price || !similarProduct.price.length) return null
      const priceRule = similarProduct.price.find(
        price => this.quantity >= price.min_limit && this.quantity <= price.max_limit
      )
      if (!priceRule) return null
      return this.showPriceWithAdd ? (priceRule.price * this.percentualProductPrice) : priceRule.price
    },

    async fetchProductImage () {
      this.loadingImage = true
      try {
        // logger.debug('Iniciando busca de imagem para produto:', this.getSelectedProduct.id)

        const imageData = await this.getProductImage(this.getSelectedProduct.id)

        // Se a resposta contém dados, trata como Blob
        if (imageData) {
          // logger.debug('Tipo de imageData:', typeof imageData)
          // logger.debug('imageData instanceof Blob:', imageData instanceof Blob)

          if (imageData instanceof Blob) {
            if (imageData.size > 0) {
              const blobUrl = URL.createObjectURL(imageData)
              this.productImage = blobUrl
              // logger.debug('URL da imagem criada com sucesso usando Blob URL')
              // logger.debug('Tamanho da imagem:', imageData.size, 'bytes')
            } else {
              // logger.warn('Blob recebido tem tamanho 0, imagem inválida')
              this.productImage = null
            }
          } else {
            logger.debug('Formato de dados não reconhecido:', imageData)
          }
        } else {
          logger.debug('Nenhum dado de imagem recebido da API')
        }
      } catch (error) {
        logger.error('Erro ao buscar imagem do produto:', error)
      } finally {
        this.loadingImage = false
      }
    },

    getHumanizedPriceRule (priceRule) {
      if ((priceRule.min_limit === 1) && (priceRule.max_limit === 999999)) {
        return `Para qualquer quantidade`
      }
      if (priceRule.min_limit === 1) {
        return `Até ${priceRule.max_limit} unidades`
      }
      if (priceRule.max_limit === 999999) {
        return `A partir de ${priceRule.min_limit} unidades`
      }
      return `De ${priceRule.min_limit} à ${priceRule.max_limit} unidades`
    },

    checkPrice () {
      this.loadingPrice = true
      this.checkProductPrice({ id: this.getSelectedProduct.id, add: 1 }).then(() => {
        this.loadingPrice = false
      })
    },

    async openProduct (search) {
      this.loading = true
      await this.getProduct({
        search,
      }).then(() => {
        this.loading = false
      })
    },

    async handleAddProduct () {
      // logger.debug('handleAddProduct')
      this.loadingProduct = true
      if (this.priceRule.price > 0) {
        // Verifica se existe um pedido selecionado, se não, cria automaticamente
        if (!this.getSelectedOrder) {
          logger.debug('Nenhum pedido selecionado, criando automaticamente...')

          this.$emit('ensureOrderExists')

          // Aguarda até que o pedido seja criado e selecionado (máximo 10 tentativas)
          let attempts = 0
          const maxAttempts = 10
          while (!this.getSelectedOrder && attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 200))
            attempts++
            logger.debug(`Tentativa ${attempts}/${maxAttempts} - Aguardando pedido...`)
          }

          // Verifica se agora existe um pedido selecionado
          if (!this.getSelectedOrder) {
            logger.error('Falha ao criar pedido automático após todas as tentativas')
            this.loadingProduct = false
            return
          }

          logger.debug('Pedido criado e selecionado com sucesso, continuando...')
        }

        if (!this.getSelectedOrder.products.find(product => product.id === this.getSelectedProduct.id)) {
          await this.checkBlockSale({
            // list_type: this.priceRule.list_type,
            // price: this.priceRule.price,
            quantity: this.quantity,
            product: this.getSelectedProduct,
          })
        } else {
          logger.debug('Abrindo modal para atualizar Qtde..')
          this.openModalQtde()
          this.loadingProduct = false
        }
      } else {
        this.setFeedback({ title: 'Desculpe, produto indisponível no momento', message: '', type: 'error' })
        this.loadingProduct = false
      }
    },

    async checkBlockSale (product) {
      try {
        let formatedProduct = product
        // logger.debug('Iniciando a verificação de estoque do produto: ', formatedProduct)
        if (!formatedProduct.price || !formatedProduct.list_type) {
          // logger.warn('nao possui price e listtype: ', formatedProduct)
          if (this.getSelectedProduct.id === formatedProduct.product.id) {
            formatedProduct = {
              // list_type: this.priceRule.list_type,
              // price: this.priceRule.price,
              quantity: formatedProduct.quantity,
              product: this.getSelectedProduct,
            }
          }
        }
        this.updateProduct = formatedProduct

        if (this.updateProduct && this.updateProduct.quantity !== this.quantity) {
          this.quantity = this.updateProduct.quantity
        }

        if (this.stockFavoriteCompany && this.quantity > this.stockFavoriteCompany.quantity) {
          logger.debug('quantidade requisitada maior que estoque preferencial')

          await this.handleBlockSale(formatedProduct)
          this.loadingProduct = false
        } else {
          // logger.debug('A qtde possui no estoque, irá inserir agora..')
          await this.addOrUpdateProduct(formatedProduct)
        }
      } catch (error) {
        logger.error('Erro ao verificar o bloqueio de venda:', error)
      }
    },

    handleBlockSale (product) {
      switch (this.getBlockSale) {
        case 0:
          return this.addOrUpdateProduct(product)
        case 1:
          logger.debug('É maior que todo estoque: ', this.quantity > this.totalStockQuantity)
          if ((this.totalStockQuantity && this.quantity > this.totalStockQuantity) || this.totalStockQuantity === 0) {
            return this.setFeedback({ title: 'Não possui estoque disponível', message: 'Desculpe, mas não há mais unidades deste produto em estoque em todo Estado.', type: 'error' })
          }
          if (product.insertionQueue) {
            logger.debug('Inserindo em fila..', product)
            return this.addOrUpdateProduct(product)
          }

          return this.openModalConfirmation()
        case 2:
          return this.setFeedback({ title: 'Não possui estoque disponível', message: 'Desculpe, mas não há mais unidades deste produto em estoque na filial preferencial.', type: 'error' })
        default:
          return []
      }
    },

    async addOrUpdateProduct (product, modalStatus) {
      this.loadingProduct = true
      const cartItem = this.getSelectedOrder.products.find(item => item.id === product.product.id)

      if (!cartItem) {
        // logger.debug('Será inserido o produto..', product)
        await this.addProductToOrder(product, modalStatus)
      } else if (this.getSelectedOrder.products.find(item => item.id === product.product.id).quantity !== product.quantity) {
        // logger.debug('Será atualizado  o produto..', product)
        await this.updateQtdeProductToOrder(product, modalStatus)
      } else {
        // logger.debug('Produto já existe no carrinho e possui o mesma quantidade')
        this.finallyOperationProduct(modalStatus)
      }
    },

    async addProductToOrder (product, modalStatus) {
      await this.addProduct({
        // list_type: product.list_type,
        // price: product.price,
        quantity: product.quantity,
        product: product.product,
      }).then(() => {
        if (!product.insertionQueue) { this.setFeedback({ title: 'Produto adicionado com sucesso', message: '', type: 'success' }) }
        this.finallyOperationProduct(modalStatus)
      }).catch((error) => {
        const message = getUserErrorMessage(error, 'Erro ao adicionar o produto ao carrinho')
        // logger.error('Erro ao realizar a operação com o produto insert:', error)
        this.setFeedback({ title: 'Erro ao adicionar o produto ao carrinho', message: message, type: 'error' })
      }).finally(() => {
        this.loadingProduct = false
      })
    },

    async updateQtdeProductToOrder (product, modalStatus) {
      logger.debug('updateQtdeProduct: ', product)
      await this.updateProductQuantity(product).then(() => {
        this.setFeedback({ title: 'Produto atualizado com sucesso', message: '', type: 'success' })
        this.finallyOperationProduct(modalStatus)
      }).catch((error) => {
        const message = getUserErrorMessage(error, 'Erro ao atualizar o produto do carrinho')
        this.setFeedback({ title: 'Erro ao atualizar o produto do carrinho', message: message, type: 'error' })
      }).finally(() => {
        this.loadingProduct = false
      })
    },

    finallyOperationProduct (modalStatus) {
      this.loadingProduct = false
      this.updateProduct = null
      this.$emit('closeSideBarModalWithAnimation')
      if (modalStatus === 'confirm') {
        this.$refs.modalConfirmation.loading = false
        this.$refs.modalConfirmation.closeModal()
      }
    },

    openImageModal () {
      this.showImageModal = true
    },

    openModalQtde () {
      this.showModalQtde = true
    },

    openModalConfirmation () {
      this.showModalConfirm = true
    },

    handleOpenPartsProducts () {
      this.$emit('openPartsProductsFromSideBar')
    },

    togglePriceWithAdd () {
      this.toggleShowPriceWithAdd()
    },

  },
}
</script>

<template>
  <div :class="$classes['product-detail']">
    <div :class="$classes['product-info']">
      <div :class="$classes['product-image-wrapper']">
        <content-loading v-if="loadingImage" :height="'100%'" />
        <icon v-else-if="!productImage" name="image" :class="$classes['product-image-icon']" />
        <img v-else :src="productImage" :class="$classes['product-image']" />
        <action variant="flat" color="primary" uppercase :class="$classes['product-image-button']"
          @click="openImageModal">
          <icon name="search-plus" btn-icon />
          Ampliar
        </action>
      </div>
      <div :class="$classes['product-info-content']">
        <div :class="$classes['product-info-columns']">
          <div :class="$classes['product-info-column']">
            <span :class="$classes['product-info-label-bold']">Código</span>
            <span :class="$classes['product-info-value-bold']">{{ getSelectedProduct.id }}</span>
          </div>
          <div :class="$classes['product-info-column']">
            <span :class="$classes['product-info-label']">Fábrica</span>
            <span :class="$classes['product-info-value']">{{ getSelectedProduct.factory }}</span>
          </div>
          <div :class="$classes['product-info-column']">
            <span :class="$classes['product-info-label']">NCM</span>
            <span :class="$classes['product-info-value']">{{ getSelectedProduct.ncm }}</span>
          </div>
        </div>
        <div :class="$classes['product-info-columns']">
          <div :class="$classes['product-info-column']">
            <span :class="$classes['product-info-label']">Descrição</span>
            <span :class="$classes['product-info-value']">{{ getSelectedProduct.description }}</span>
          </div>
        </div>
        <div :class="$classes['product-info-columns']">
          <div :class="$classes['product-info-column']">
            <span :class="$classes['product-info-label']">Fração Venda</span>
            <span :class="$classes['product-info-value']">{{ getSelectedProduct.fraction }}</span>
          </div>
          <div :class="$classes['product-info-column']">
            <span :class="$classes['product-info-label']">Grupo Lista</span>
            <span :class="$classes['product-info-value']">{{ getSelectedProduct.group_list }}</span>
          </div>
        </div>

        <!-- Mensagem do produto -->
        <div v-if="getSelectedProduct.msg" :class="$classes['product-message-container']">
          <div :class="$classes['product-message-content']">
            <icon name="info-circle" :class="$classes['product-message-icon']" />
            <span :class="$classes['product-message-text']">{{ getSelectedProduct.msg }}</span>
          </div>
        </div>

        <div :class="$classes['product-info-column']">
          <!-- <action :class="$classes['product-check-price']" variant="flat" color="primary" uppercase bold
            :disabled="loadingPrice" @click="checkPrice">
            <icon name="search-dollar" btn-icon />
            Consultar preço
          </action> -->
          <action v-if="getEnableParts" :class="$classes['product-check-price']" variant="flat" color="primary"
            uppercase bold @click="handleOpenPartsProducts">
            <icon name="wrench" btn-icon />
            Conjunto de Peças
          </action>
        </div>
      </div>
    </div>
    <div :class="$classes['product-price']">
      <content-loading v-if="loadingPrice" :class="$classes['product-price-content-loading']" />
      <template v-else-if="getSelectedProduct.price != null && !loadingPrice">
        <div v-if="getSelectedProduct.product_ipi" :class="$classes['product-price-header']">
          <span v-if="getSelectedProduct.product_ipi === 1 && ipiAliquota > 0"
            :class="{ [$classes['product-price-label']]: true, [$classes['red']]: true }">
            Este produto terá cobrança de IPI de {{ ipiAliquota }}%
          </span>
          <span v-else-if="getSelectedProduct.product_ipi === 1 && ipiAliquota === 0"
            :class="{ [$classes['product-price-label']]: true, [$classes['red']]: true }">
            Produto sujeito a IPI — Alíquota de IPI não informada
          </span>
        </div>
        <div :class="$classes['product-price-header']">
          <span :class="$classes['product-price-label']">Qtd.:</span>
          <number-field light v-model="quantity" :min="getSelectedProduct.fraction" :step="getSelectedProduct.fraction"
            :disabled="isPriceUnavailable || disableBtnAddProduct" />
          <div>

          </div>
          <action ref="priceToggleBtn" :class="[$classes['price-toggle-btn'], computedClasses]"
            :disabled="!getPriceAdd || disableBtnAddProduct" variant="flat" color="secondary"
            v-tooltip="getPriceAdd === 0 || !getPriceAdd ? { content: 'Acréscimo financeiro não configurado', delay: { show: 100, hide: 100 }, trigger: 'hover', autoHide: true } : ''"
            @click="togglePriceWithAdd">
            <strong>%</strong>
          </action>
          <span v-if="!isPriceUnavailable" :class="$classes['product-price-subtotal']">Subtotal:
            <strong>{{ subtotal | currency }}</strong></span>
          <action v-if="$media.mobile" variant="contained" color="primary" :class="$classes['product-price-btn']"
            :disabled="disableBtnAddProduct || !isMultipleOfFraction" :loading="isAddProductLoading"
            :aria-label="addProductLabel" @click="handleAddProduct">
            <icon v-if="!isLoadingOrderCart" name="plus" />
          </action>
          <action v-else variant="contained" color="primary" :class="$classes['product-price-btn']" upstream
            :disabled="disableBtnAddProduct || !isMultipleOfFraction" :loading="isAddProductLoading"
            @click="handleAddProduct">
            <icon v-if="!isLoadingOrderCart" name="plus" btnIcon />
            {{ addProductLabel }}
          </action>
        </div>
        <!-- O carregamento fica no próprio botão; aqui só o erro, que precisa
             dizer ao usuário o que fazer. -->
        <div v-if="getOrderCartError" :class="$classes['cart-alert']" role="alert">
          <icon name="triangle-exclamation-solid" :class="$classes['cart-alert-icon']" />
          <span>{{ cartUnavailableMessage }}</span>
        </div>
        <div v-if="getSelectedProduct.product_ipi && getSelectedProduct.ipi_aliquota != 0"
          :class="[$classes['product-price-header'], $classes['ipi']]">
          <span :class="[$classes['product-price-label'], $classes['ipi-value']]">Valor IPI:
            <strong>{{ totalIpi | currency }}</strong></span>
          <span :class="$classes['product-price-label']">Subtotal com IPI: <strong>{{ subtotalWithIpi |
            currency }}</strong></span>
        </div>
        <div :class="$classes['product-price-content']">
          <div v-if="!isPriceUnavailable" :class="$classes['product-price-rules']">
            <template v-for="(priceRule, index) in getSelectedProduct.price">
              <div :key="index" :class="$classes['product-price-rule-item']">
                <span :class="$classes['product-price-rule-label']">Opção {{ index + 1 }}</span>
                <span :class="$classes['product-price-rule-quantity']">{{
                  getHumanizedPriceRule(priceRule)
                  }}</span>
                <span :class="$classes['product-price-rule-value']">{{
                  showPriceWithAdd ? (priceRule.price * percentualProductPrice) : priceRule.price | currency
                  }}</span>
              </div>
            </template>
          </div>
          <div v-else-if="isPriceUnavailable" :class="$classes['product-price-unavailable-container']">
            <icon name="triangle-exclamation-solid" :class="$classes['unavailable-icon']" />
            <div :class="$classes['unavailable-content']">
              <span :class="$classes['unavailable-title']">Preço não disponível -</span>
              <span :class="$classes['unavailable-message']">Entre em contato vendedor</span>
            </div>
          </div>
        </div>
      </template>
    </div>
    <div :class="$classes['product-highlight-columns']">
      <div :class="$classes['product-highlight-info']">
        <div v-if="hasStockObjects"
          :class="[$classes['product-info-block-stock'], $classes['product-info-block-stock-empty']]">
          <icon name="blocked-box" btn-icon :class="$classes['icon-blocked-box']" />
          {{ getSelectedProduct.stock.message }}
        </div>
        <div v-else-if="getSelectedProduct.stock.length === 0"
          :class="[$classes['product-info-block-stock'], $classes['product-info-block-stock-empty']]">
          <icon name="blocked-box" btn-icon :class="$classes['icon-blocked-box']" />
          Sem estoque informado
        </div>
        <table v-else-if="getSelectedProduct.stock.length > 0 && !hasStockObjects">
          <thead>
            <tr>
              <th>Empresa</th>
              <th>Em estoque</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(stock, index) in getSelectedProduct.stock" :key="index">
              <td>{{ stock.company }}</td>
              <td>{{ stock.quantity }}</td>
            </tr>
            <tr>
              <td><strong>Total:</strong></td>
              <td>{{ totalStockQuantity }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div :class="$classes['product-highlight-info']">
        <table :class="$classes['product-similar-table']" style="">
          <thead>
            <tr>
              <th>Prod. Equivalente</th>
              <th>Indústria</th>
              <th>Preço</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(similarProduct, index) in getSelectedProduct.similar" :key="index">
              <td>
                <action :class="$classes['order-cart-products-btn']" @click="openProduct(similarProduct.id)">
                  {{ similarProduct.id }}
                </action>
              </td>
              <td style="">
                {{ similarProduct.factory }}
              </td>
              <td>
                {{ getSimilarProductPrice(similarProduct) | currency }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div :class="$classes['product-highlight-info']">
        <table>
          <thead>
            <tr>
              <th>Código</th>
              <th>Original</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(originalProduct, index) in getSelectedProduct.original" :key="index">
              <td>{{ originalProduct.code }}</td>
              <td>{{ originalProduct.original }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <modal-confirmation ref="modalConfirmation" :active.sync="showModalConfirm"
      :stock="stockFavoriteCompany && stockFavoriteCompany.quantity" :product="updateProduct"
      @toOrder="addOrUpdateProduct" />

    <modal-product-image :active.sync="showImageModal" :image="productImage" />

    <modal-update-cart-item-quantity ref="modalUpdateQtde" :active.sync="showModalQtde" :product="getSelectedProduct"
      :currentQuantity="quantity" @updateQuantity="checkBlockSale" />
  </div>
</template>

<style module>
.header-title {
  text-transform: uppercase;
  font-size: 18px;
  letter-spacing: 0.1em;
  color: #292929;
  margin-bottom: 20px;
  padding: 0px 30px 0px 0px;
}

.product-check-price {
  margin-bottom: 5px;
  margin-right: 20px;
  align-items: flex-start;
}

.product-detail {
  /* margin-top: 30px; */
}

.product-info {
  display: flex;
  margin-bottom: 20px;
  width: 100%;
}

.product-image-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 2px;
  width: 30%;
  height: 250px;
  margin-right: 20px;
  overflow: hidden;
  color: #A1A1A1;
  position: relative;
  max-width: 160px;
  min-width: 160px;
}

.product-image-wrapper::before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 48.75%, #FFFFFF 100%);
  z-index: 2;
}

.product-image-icon {
  width: 16px;
  height: 16px;
}

.product-image {
  display: block;
  width: 100%;
  height: auto;
}

.product-image-button {
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 4;
}

.product-info-columns {
  display: flex;
  align-items: flex-start;
  margin-bottom: 30px;
}

.product-info-column+.product-info-column {
  margin-left: 25px;
}

.product-info-label {
  display: block;
  text-transform: uppercase;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: #a1a1a1;
  margin-bottom: 10px;
}

.product-info-label-bold {
  display: block;
  text-transform: uppercase;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  color: #a1a1a1;
  margin-bottom: 10px;
}

.product-info-value {
  display: block;
  font-size: 14px;
  line-height: 18px;
  color: #292929;
  text-transform: uppercase;
}

.product-info-value-bold {
  display: block;
  font-size: 14px;
  font-weight: bold;
  line-height: 18px;
  color: #292929;
  text-transform: uppercase;
}

.product-info-block-stock {
  display: flex;
  text-transform: uppercase;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  gap: 10px;
  color: #D14040;
  text-align: left;
  justify-content: flex-start;
  align-items: center;
}

.product-info-block-stock-empty {
  min-height: 72px;
  width: 100%;
  padding: 14px 18px;
  background: #E7EAEC;
  border-radius: 2px;
  letter-spacing: 0.03em;
}

.icon-blocked-box {
  width: 28px;
  height: 28px;
  margin-right: 8px;
  flex-shrink: 0;
}

.product-price {
  margin-bottom: 10px;
}

.product-price-header {
  background: #E7EAEC;
  height: 40px;
  display: flex;
  align-items: center;
  padding-left: 20px;
  border-radius: 2px;
}

.product-price-header.ipi {
  gap: 12px;
}

.cart-alert {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 12px;
  border-left: 3px solid #D14040;
  border-radius: 2px;
  background: #FDECEC;
  font-size: 12px;
  line-height: 16px;
  color: #292929;
}

.cart-alert-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: #D14040;
}

.product-price-label {
  font-size: 12px;
  color: #292929;
  text-transform: uppercase;
  margin-right: 10px;
}

.product-price-label.ipi-value {
  min-width: 130px;
}

.product-price-label.red {
  color: red;
  font-weight: 500;
}

.product-price-subtotal {
  font-size: 12px;
  color: #292929;
  text-transform: uppercase;
  margin-left: 15px;
  flex: 1;
}

.product-price-unavailable-container {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: 15px;
  flex: 1;
  /* background: #E7EAEC; */
}

.unavailable-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: #D14040;
}

.unavailable-content {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
}

.unavailable-title {
  font-size: 13px;
  font-weight: 600;
  color: #292929;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.unavailable-message {
  font-size: 11px;
  color: #6d6d6d;
  line-height: 1.4;
  font-weight: 500;
}

.product-price-btn {
  height: 40px;
  padding: 0 15px;
  margin-left: auto;
}

.product-addOrder-btn {
  height: 40px;
  padding: 0 15px;
  float: right;
}

.product-price-content {
  border-radius: 2px;
  background: #fff;
  padding: 20px;
}

.product-price .product-price-content-loading {
  height: 98px !important;
}

.product-price-rule-item {
  display: flex;
}

.product-price-rule-item+.product-price-rule-item {
  margin-top: 15px;
}

.product-price-rule-label {
  font-size: 12px;
  color: #a1a1a1;
  text-transform: uppercase;
  width: 130px;
}

.product-price-rule-quantity {
  font-size: 12px;
  color: #292929;
  text-transform: uppercase;
  flex: 1;
}

.product-price-rule-value {
  width: 90px;
  font-size: 12px;
  color: #292929;
}

.product-highlight-info {
  background: #E7EAEC;
  padding: 20px;
  border-radius: 2px;
}

.product-highlight-columns {
  display: flex;
  margin-top: 10px;
}

.product-highlight-columns .product-highlight-info {
  flex: 1;
  width: 50%;
}

.product-highlight-columns .product-highlight-info+.product-highlight-info {
  margin-left: 10px;
}

.product-highlight-info-item {
  display: flex;
  align-items: center;
}

.product-highlight-info-item+.product-highlight-info-item {
  margin-top: 15px;
}

/* Estilos para a mensagem do produto */
.product-message-container {
  margin: 20px 0;
  padding: 16px 20px;
  background: linear-gradient(135deg, #fff8e1 0%, #fff8e1 100%);
  /* border: 1px solid #dee2e6; */
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.product-message-container:hover {
  /* box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08); */
}

.product-message-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.product-message-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: #495057;
  margin-top: 2px;
}

.product-message-text {
  color: #495057;
  font-size: 14px;
  line-height: 1.5;
  font-weight: 500;
  margin: 0;
  flex: 1;
}

.product-highlight-label {
  font-size: 12px;
  line-height: 16px;
  text-transform: uppercase;
  color: #a1a1a1;
  width: 130px;
  margin-right: 5px;
}

.product-highlight-value {
  flex: 1;
  font-size: 12px;
  line-height: 16px;
  text-transform: uppercase;
  color: #292929;
}

.product-highlight-info table {
  width: 100%;
}

.product-highlight-info .product-similar-table th:nth-child(1),
.product-highlight-info .product-similar-table td:nth-child(1) {
  width: auto;
}

.product-highlight-info .product-similar-table th:nth-child(2),
.product-highlight-info .product-similar-table td:nth-child(2) {
  width: auto;
}

.product-highlight-info .product-similar-table th:nth-child(3),
.product-highlight-info .product-similar-table td:nth-child(3) {
  width: auto;
}

.product-highlight-info th {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: #a1a1a1;
  width: 50%;
  text-align: left;
  height: 30px;
}

.product-highlight-info td {
  font-size: 12px;
  color: #292929;
  text-align: left;
  height: 30px;
}

.order-cart-products-btn {
  text-align: left;
  display: inline-block;
  font-size: 14px;
  color: #4A94D4;
  text-decoration: underline;
  line-height: 20px;
}

.price-toggle-btn {
  background: #FFF;
  color: #A2A2A2;
  width: 40px;
  height: 26px;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease-in-out;
  margin: 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.price-toggle-btn:hover {
  background: #f8f9fa;
  color: #6c757d;
  border-color: #bdbdbd;
}

.price-toggle-btn.active {
  background: #dc3545;
  color: #FFF;
  border-color: #dc3545;
}

.price-toggle-btn.active:hover {
  background: #c82333;
  border-color: #bd2130;
  color: #FFF;
}

.price-toggle-icon {
  width: 16px;
  height: 16px;
}

@media (max-width: 800px) {
  .product-highlight-columns {
    flex-direction: column;
  }

  .product-highlight-columns .product-highlight-info {
    width: 100%;
  }

  .product-highlight-columns .product-highlight-info+.product-highlight-info {
    margin-left: 0;
    margin-top: 10px;
  }
}

@media (max-width: 767px) {
  .product-info {
    flex-direction: column;
  }

  .product-check-price {
    margin-top: -15px;
    margin-bottom: 5px;
    margin-right: 20px;
    padding: 20px;
    align-items: flex-start;
  }

  .product-info-column {
    display: flex;
    flex-direction: column;
  }

  .product-image-wrapper {
    margin-bottom: 20px;
    margin-right: 0px;
    display: flex;
    flex-direction: column;
    width: -webkit-fill-available;
    max-width: -webkit-fill-available;
    height: 100vw;
  }

  /* Estilos responsivos para a mensagem do produto */
  .product-message-container {
    margin: 15px 0;
    padding: 14px 16px;
  }

  .product-message-content {
    gap: 10px;
  }

  .product-message-text {
    font-size: 13px;
  }

  /* Estilos responsivos para mensagem de preço indisponível */
  .product-price-unavailable-container {
    margin-left: 10px;
    padding: 10px 12px;
    gap: 8px;
  }

  .unavailable-icon {
    width: 18px;
    height: 18px;
  }

  .unavailable-title {
    font-size: 12px;
  }

  .unavailable-message {
    font-size: 10px;
  }
}
</style>
